import React, { useState, useRef, useEffect } from 'react';

const LoginOTPModal = ({ isOpen, onClose, onVerify, email, loading, otpToken, externalError }) => { // ✅ Added externalError prop
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  // ✅ Update error when parent passes error
  useEffect(() => {
    if (externalError) {
      setError(externalError);
      // Clear code inputs on error
      setCode(['', '', '', '', '', '']);
      // Focus first input
      inputRefs.current[0]?.focus();
    }
  }, [externalError]);

  // ✅ Clear error when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError('');
      setCode(['', '', '', '', '', '']);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Clear error when user starts typing
    if (error) {
      setError('');
    }

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const digits = pastedData.split('').filter(char => /^\d$/.test(char));
    
    const newCode = [...code];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });
    setCode(newCode);
    
    const nextEmptyIndex = newCode.findIndex(val => !val);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = () => {
    const codeString = code.join('');
    if (codeString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    
    // ✅ Validate OTP token exists
    if (!otpToken) {
      setError('Session expired. Please login again.');
      return;
    }
    
    setError('');
    onVerify(codeString); // Code will be sent in LoginForm
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-white/40 backdrop-blur-md"
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-2xl border-[1.5px] border-primary-blue shadow-2xl w-full max-w-[25rem] 2xl:max-w-md">
        <div className="relative px-8 pt-6 pb-5">
          <h2 className="text-xl font-semibold text-gray-900 text-center">
            Verify Your Identity
          </h2>
          
          <button
            onClick={onClose}
            className="absolute right-6 top-6 hover:opacity-70 transition-opacity"
            disabled={loading}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2" fill="none"/>
              <path d="M8 8L16 16M16 8L8 16" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="px-8 pb-6 pt-2 flex flex-col items-center">
          <div className="mb-4">
            <img
              src="/Icons/email-logo.svg"
              alt="Email Verification"
              className="w-20 h-20"
            />
          </div>
          
          <p className="text-center text-gray-600 text-sm mb-6">
            Please enter the 6 digit code sent to{' '}
            <span className="font-semibold text-gray-900">{email}</span>
          </p>

          {error && (
            <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-4">
              {error}
            </div>
          )}

          <div className="w-full">
            <div className="flex gap-2 justify-center mb-4">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-14 text-center text-xl font-semibold border-[1.5px] border-primary-blue rounded-lg focus:outline-none focus:border-2 focus:border-primary-blue transition-colors"
                  disabled={loading}
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="px-8 pb-6 pt-2 flex gap-3">
          <button
            onClick={handleVerify}
            disabled={loading}
            className="flex-1 py-3 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-3 bg-white border-[1.5px] border-primary-blue text-primary-blue font-semibold rounded-lg hover:bg-primary-blue hover:text-white transition-colors text-base disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginOTPModal;
